import { useReactFlow, Viewport } from 'reactflow';
import {
  CANVAS_LEFT_MARGIN,
  CANVAS_WIDTH,
  VIEWPORT_CORRECTION_TIME,
} from '@constants/canvas/general';
import {
  abandonViewportAnimation,
  claimViewportAnimation,
  isLatestViewportAnimation,
  settleViewportAnimation,
} from '@hooks/canvas/viewportAnimation';

/**
 * Pulls the viewport back inside the canvas after something changed its usable
 * width — a panel opening, a drawer closing, chrome being hidden.
 *
 * `duration` is the milliseconds to wait for that change to finish animating,
 * never a distance. The wait can outlive the component that scheduled it, so
 * React Flow may already be torn down when it resolves: both of its calls are
 * guarded individually, leaving the correction arithmetic between them free to
 * fail loudly, and the returned promise settles rather than rejecting because
 * every call site is fire-and-forget.
 */
const useResetViewport = () => {
  const { setViewport, getViewport } = useReactFlow();

  return async (duration = 0) => {
    const token = claimViewportAnimation();

    await new Promise((resolve) => {
      setTimeout(resolve, duration + 50);
    });

    if (!isLatestViewportAnimation(token)) {
      abandonViewportAnimation(token);
      return;
    }

    const canvas = document.querySelector('#canvas');

    if (!canvas) {
      abandonViewportAnimation(token);
      return;
    }

    const applyViewport = (next: Viewport) => {
      try {
        setViewport(next, { duration: VIEWPORT_CORRECTION_TIME });
        settleViewportAnimation(token);
      } catch {
        abandonViewportAnimation(token);
      }
    };

    let viewport: Viewport;

    try {
      viewport = getViewport();
    } catch {
      abandonViewportAnimation(token);
      return;
    }

    const differenceX = canvas.clientWidth - CANVAS_WIDTH * viewport.zoom;

    if (differenceX > CANVAS_LEFT_MARGIN * viewport.zoom) {
      applyViewport({ ...viewport, x: CANVAS_LEFT_MARGIN * viewport.zoom });
      return;
    }

    if (viewport.x <= differenceX) {
      applyViewport({ ...viewport, x: differenceX });
      return;
    }

    abandonViewportAnimation(token);
  };
};

export default useResetViewport;
